import React, { useState } from 'react';
import { dryEditorClass } from '.';
import schema from './schema';
import constants from '../../constants';
const { EDITOR_COMMAND_NAME_MAP } = constants;

// Returns the from/to positions of a mark around a resolved position, or null
const getMarkRange = ($pos, markType) => {
  const parent = $pos.parent;
  const start = $pos.start();
  let from = $pos.pos;
  let to = $pos.pos;
  let found = false;

  parent.forEach((child, offset) => {
    if (!child.isText) return;
    const childFrom = start + offset;
    const childTo = childFrom + child.nodeSize;
    if (child.marks.some(m => m.type === markType)) {
      if (childFrom <= $pos.pos && $pos.pos <= childTo) {
        from = childFrom;
        to = childTo;
        found = true;
      }
    }
  });

  return found ? { from, to } : null;
};

// Reads the current editor selection to pre-fill the dialog
export const getLinkDialogState = () => {
  const state = dryEditorClass.getEditorState?.();
  if (!state) return { initialUrl: '', initialText: '', isEditing: false };

  const { selection } = state;
  const { $from, $to, empty } = selection;
  const anchorMark = schema.marks.anchor;

  let initialUrl = '';
  let initialText = '';
  let isEditing = false;

  if (empty) {
    // $from.marks() respects inclusive:false and won't return the anchor mark
    // when the cursor sits at the start/end boundary of a link. Use
    // getMarkRange (which walks parent text nodes) so boundary placement works.
    const range = getMarkRange($from, anchorMark);
    if (range) {
      isEditing = true;
      initialText = state.doc.textBetween(range.from, range.to);
      state.doc.nodesBetween(range.from, range.to, (node) => {
        if (!initialUrl && node.isText) {
          const m = node.marks.find(mk => mk.type === anchorMark);
          if (m) initialUrl = m.attrs.href || '';
        }
      });
    }
  } else {
    initialText = state.doc.textBetween($from.pos, $to.pos);
    if (state.doc.rangeHasMark($from.pos, $to.pos, anchorMark)) {
      isEditing = true;
      state.doc.nodesBetween($from.pos, $to.pos, (node) => {
        if (!initialUrl && node.isText) {
          const m = node.marks.find(mk => mk.type === anchorMark);
          if (m) initialUrl = m.attrs.href || '';
        }
      });
    }
  }

  return { initialUrl, initialText, isEditing };
};

// Returns a warning object for the URL field, or null if the URL looks valid.
// type 'suggestion' = missing protocol (auto-fixable)
// type 'error'      = structurally invalid URL
const getUrlWarning = (url) => {
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Missing protocol — most common mistake (user typed "example.com")
  if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//i.test(trimmed)) {
    const suggestion = `https://${trimmed}`;
    return { type: 'suggestion', suggestion };
  }

  try {
    new URL(trimmed);
    return null; // valid
  } catch {
    return { type: 'error', suggestion: null };
  }
};

const inputStyle = {
  background: '#ffffff',
  border: '1px solid #d0d0d0',
  borderRadius: '4px',
  color: '#333333',
  padding: '6px 8px',
  fontSize: '0.875rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  color: '#666666',
  fontSize: '0.75rem',
  marginBottom: '4px',
  display: 'block',
};

const btnBase = {
  padding: '6px 14px',
  borderRadius: '4px',
  fontSize: '0.875rem',
  cursor: 'pointer',
  border: '1px solid #d0d0d0',
};

const LinkDialogContent = ({ initialUrl = '', initialText = '', isEditing = false, sendModalState }) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);

  const warning = getUrlWarning(url);
  const canApply = url.trim().length > 0 && warning?.type !== 'error';

  const handleApply = () => {
    if (!canApply) return;
    const finalUrl = warning?.type === 'suggestion' ? warning.suggestion : url.trim();
    dryEditorClass.execCommands([{
      commandName: EDITOR_COMMAND_NAME_MAP.link,
      options: { href: finalUrl, title: text.trim() || null },
    }]);
    sendModalState({ type: 'closeModal' });
  };

  const handleRemove = () => {
    dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.unlink }]);
    sendModalState({ type: 'closeModal' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleApply(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} onKeyDown={handleKeyDown}>
      <div>
        <label style={labelStyle} htmlFor="link-text-input">Text</label>
        <input
          id="link-text-input"
          style={inputStyle}
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Link text"
        />
      </div>
      <div>
        <label style={labelStyle} htmlFor="link-url-input">URL</label>
        <input
          id="link-url-input"
          style={{ ...inputStyle, borderColor: warning?.type === 'error' ? '#d9534f' : '#d0d0d0' }}
          type="text"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://"
          autoFocus
        />
        {warning && (
          <div style={{ marginTop: '4px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {warning.type === 'suggestion' && (
              <>
                <span style={{ color: '#8a6d3b' }}>Missing protocol.</span>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a73e8', padding: 0, fontSize: '0.75rem', textDecoration: 'underline' }}
                  onClick={() => setUrl(warning.suggestion)}
                >
                  Use {warning.suggestion.length > 40 ? warning.suggestion.slice(0, 40) + '…' : warning.suggestion}
                </button>
              </>
            )}
            {warning.type === 'error' && (
              <span style={{ color: '#d9534f' }}>This URL appears to be invalid.</span>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        {isEditing && (
          <button
            type="button"
            style={{ ...btnBase, background: 'transparent', color: '#d9534f', borderColor: '#d9534f', marginRight: 'auto' }}
            onClick={handleRemove}
          >
            Remove link
          </button>
        )}
        <button
          type="button"
          style={{ ...btnBase, background: 'transparent', color: '#555555' }}
          onClick={() => sendModalState({ type: 'closeModal' })}
        >
          Cancel
        </button>
        <button
          type="button"
          style={{
            ...btnBase,
            background: canApply ? '#1a73e8' : '#e8e8e8',
            color: canApply ? '#ffffff' : '#aaaaaa',
            borderColor: canApply ? '#1a73e8' : '#e8e8e8',
            cursor: canApply ? 'pointer' : 'not-allowed',
          }}
          onClick={handleApply}
          disabled={!canApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

// Call this from anywhere that has sendModalState (toolbar, keyboard shortcut)
export const openLinkDialog = (sendModalState) => {
  const { initialUrl, initialText, isEditing } = getLinkDialogState();

  sendModalState({
    type: 'updateModal',
    data: {
      title: isEditing ? 'Edit Link' : 'Insert Link',
      showCloseModal: true,
      content: (
        <LinkDialogContent
          initialUrl={initialUrl}
          initialText={initialText}
          isEditing={isEditing}
          sendModalState={sendModalState}
        />
      ),
      primaryButton: { show: false },
      secondaryButton: { show: false },
    },
  });
};

export default LinkDialogContent;
